import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail, notifyDelete } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import MemberDialog, { emptyMemberForm } from "@/components/MemberDialog";
import MemberCard from "@/components/MemberCard";
import ExportButton from "@/components/ExportButton";
import MemberFilters, { filterMembers } from "@/components/MemberFilters";

export default function Members() {
  const [params, setParams] = useSearchParams();
  const activeTab = params.get("section") || "all";
  const [members, setMembers] = useState([]);
  const [overdueMap, setOverdueMap] = useState({});
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyMemberForm);

  const load = useCallback(async () => {
    const { data } = await api.get("/members");
    setMembers(data);
    try {
      const od = await api.get("/fees/overdue");
      const map = {};
      od.data.forEach((m) => (map[m.id] = m));
      setOverdueMap(map);
    } catch (err) {
      toast.error("Could not load overdue fee status");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => filterMembers(members, activeTab, search), [members, activeTab, search]);

  const openNew = () => { setEditing(null); setForm(emptyMemberForm); setOpen(true); };
  const openEdit = (m) => { setEditing(m); setForm({ ...emptyMemberForm, ...m }); setOpen(true); };

  const save = async () => {
    try {
      const payload = { ...form, fee_amount: Number(form.fee_amount) || 0 };
      if (editing) {
        await api.put(`/members/${editing.id}`, payload);
        toast.success("Member updated");
      } else {
        await api.post("/members", payload);
        toast.success("Member added");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Remove this member and their records?")) return;
    const { data } = await api.delete(`/members/${id}`);
    notifyDelete(data, "Member removed");
    load();
  };

  return (
    <div className="space-y-6" data-testid="members-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Register</div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight" style={{ fontFamily: "Outfit" }}>Members</h1>
        </div>
        <div className="flex gap-2">
          <ExportButton path="/export/members.csv" filename="members.csv" testid="export-members-btn" />
          <Button data-testid="add-member-btn" onClick={openNew} className="min-h-[44px]">
            <Plus className="w-4 h-4 mr-1.5" /> Add member
          </Button>
        </div>
      </div>

      <MemberFilters members={members} activeTab={activeTab} search={search} onSearch={setSearch}
        onTabChange={(v) => setParams(v === "all" ? {} : { section: v })} />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.length === 0 && (
          <div className="col-span-full text-center py-12 text-muted-foreground" data-testid="no-members">No members yet — add one to get started.</div>
        )}
        {filtered.map((m) => (
          <MemberCard key={m.id} member={m} overdueInfo={overdueMap[m.id]} onEdit={openEdit} onDelete={del} />
        ))}
      </div>

      <MemberDialog open={open} onOpenChange={setOpen} editing={editing} form={form} setForm={setForm} onSave={save} />
    </div>
  );
}
